import type { AuthTokens } from '@fluenceur/types';
import { clearImpersonationCookie } from './impersonation';

/**
 * Token Manager Service for JWT Bearer Token Authentication
 * Handles storing, retrieving, and managing authentication tokens in cookies
 */

interface TokenMetadata {
  type: 'Bearer';
  expiresIn: number; // Duration in seconds
  expiresAt: number; // Calculated expiration timestamp
}

interface TokenData extends AuthTokens {
  expiresAt?: number; // Calculated expiration timestamp
}

class TokenManager {
  private readonly ACCESS_TOKEN_KEY = 'fl_access_token';
  private readonly REFRESH_TOKEN_KEY = 'fl_refresh_token';
  private readonly TOKEN_DATA_KEY = 'fl_token_data';

  /**
   * Cookie settings for authentication tokens
   */
  private getCookieOptions(maxAge?: number): string {
    const secure = typeof window !== 'undefined' && window.location.protocol === 'https:';
    const maxAgeValue = maxAge || 30 * 24 * 60 * 60; // 30 days default
    return `path=/; SameSite=Lax; max-age=${maxAgeValue}${secure ? '; Secure' : ''}`;
  }

  /**
   * Set a cookie value (client-side)
   */
  private setCookie(name: string, value: string, maxAge?: number): void {
    if (typeof document === 'undefined') {
      console.warn('[TOKEN-MANAGER] Cannot set cookies on server side');
      return;
    }
    document.cookie = `${name}=${encodeURIComponent(value)}; ${this.getCookieOptions(maxAge)}`;
  }

  /**
   * Get a cookie value (client-side)
   */
  private getCookie(name: string): string | null {
    if (typeof document === 'undefined') {
      return null;
    }

    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return decodeURIComponent(c.substring(nameEQ.length, c.length));
    }
    return null;
  }

  /**
   * Delete a cookie (client-side)
   */
  private deleteCookie(name: string): void {
    if (typeof document === 'undefined') {
      return;
    }
    document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
  }

  /**
   * Store authentication tokens in cookies
   */
  setTokens(tokens: AuthTokens): void {
    try {
      // New credentials end the previous session. An admin callback explicitly
      // marks impersonation after this call; token rotation uses updateAccessToken.
      clearImpersonationCookie();
      const tokenMetadata: TokenMetadata = {
        type: tokens.type,
        expiresIn: tokens.expiresIn,
        expiresAt: Date.now() + tokens.expiresIn * 1000, // Convert to timestamp
      };

      // Store tokens individually with appropriate expiration
      // Note: Access token cookie max-age is set to 30 days (same as refresh token)
      // to allow automatic refresh attempts. The token itself still expires in 30 minutes,
      // but the cookie persists to enable transparent token refresh.
      this.setCookie(this.ACCESS_TOKEN_KEY, tokens.accessToken, 30 * 24 * 60 * 60); // 30 days - same as refresh token
      this.setCookie(this.REFRESH_TOKEN_KEY, tokens.refreshToken, 30 * 24 * 60 * 60); // 30 days

      // Store only metadata (no tokens to avoid redundancy)
      this.setCookie(this.TOKEN_DATA_KEY, JSON.stringify(tokenMetadata), 30 * 24 * 60 * 60);

      console.log('[TOKEN-MANAGER] Tokens stored successfully in cookies');
    } catch (error) {
      console.error('[TOKEN-MANAGER] Failed to store tokens:', error);
    }
  }

  /**
   * Get the current access token from cookies
   * Note: Returns token even if expired - let the server validate it
   * This prevents race conditions during proactive refresh
   */
  getAccessToken(): string | null {
    try {
      return this.getCookie(this.ACCESS_TOKEN_KEY);
    } catch (error) {
      console.error('[TOKEN-MANAGER] Failed to get access token:', error);
      return null;
    }
  }

  /**
   * Get the current access token only if it's valid (not expired)
   * Use this for client-side checks where you need a valid token
   */
  getValidAccessToken(): string | null {
    try {
      const token = this.getCookie(this.ACCESS_TOKEN_KEY);

      // Check if token is expired
      if (token && this.isTokenExpired()) {
        console.log('[TOKEN-MANAGER] Access token is expired');
        return null;
      }

      return token;
    } catch (error) {
      console.error('[TOKEN-MANAGER] Failed to get access token:', error);
      return null;
    }
  }

  /**
   * Get the current refresh token from cookies
   */
  getRefreshToken(): string | null {
    try {
      return this.getCookie(this.REFRESH_TOKEN_KEY);
    } catch (error) {
      console.error('[TOKEN-MANAGER] Failed to get refresh token:', error);
      return null;
    }
  }

  /**
   * Get token metadata from cookies (without actual tokens)
   */
  getTokenMetadata(): TokenMetadata | null {
    try {
      const data = this.getCookie(this.TOKEN_DATA_KEY);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('[TOKEN-MANAGER] Failed to get token metadata:', error);
      return null;
    }
  }

  /**
   * Get complete token data from cookies (reconstructed from individual cookies + metadata)
   */
  getTokenData(): TokenData | null {
    try {
      const accessToken = this.getCookie(this.ACCESS_TOKEN_KEY);
      const refreshToken = this.getCookie(this.REFRESH_TOKEN_KEY);
      const metadata = this.getTokenMetadata();

      if (!accessToken || !refreshToken || !metadata) {
        return null;
      }

      return {
        accessToken,
        refreshToken,
        type: metadata.type,
        expiresIn: metadata.expiresIn,
        expiresAt: metadata.expiresAt,
      };
    } catch (error) {
      console.error('[TOKEN-MANAGER] Failed to get token data:', error);
      return null;
    }
  }

  /**
   * Check if the current access token is expired
   */
  isTokenExpired(): boolean {
    try {
      const metadata = this.getTokenMetadata();
      if (!metadata || !metadata.expiresAt) {
        return true;
      }

      return Date.now() >= metadata.expiresAt;
    } catch (error) {
      console.error('[TOKEN-MANAGER] Failed to check token expiration:', error);
      return true;
    }
  }

  /**
   * Check if token is close to expiring (within 5 minutes)
   */
  isTokenExpiring(minutesBeforeExpiry: number = 5): boolean {
    try {
      const metadata = this.getTokenMetadata();
      if (!metadata || !metadata.expiresAt) {
        return true;
      }

      const warningTime = minutesBeforeExpiry * 60 * 1000; // Convert to milliseconds
      return Date.now() >= metadata.expiresAt - warningTime;
    } catch (error) {
      console.error('[TOKEN-MANAGER] Failed to check token expiring:', error);
      return true;
    }
  }

  /**
   * Get time remaining until token expires (in seconds)
   */
  getTimeUntilExpiry(): number {
    try {
      const metadata = this.getTokenMetadata();
      if (!metadata || !metadata.expiresAt) {
        return 0;
      }

      const timeRemaining = Math.max(0, Math.floor((metadata.expiresAt - Date.now()) / 1000));
      return timeRemaining;
    } catch (error) {
      console.error('[TOKEN-MANAGER] Failed to get time until expiry:', error);
      return 0;
    }
  }

  /**
   * Check if user has valid authentication
   */
  isAuthenticated(): boolean {
    const accessToken = this.getAccessToken();
    const refreshToken = this.getRefreshToken();

    // Must have at least a refresh token to be considered authenticated
    return !!(accessToken || refreshToken);
  }

  /**
   * Get Authorization header value
   */
  getAuthorizationHeader(): string | null {
    const token = this.getAccessToken();
    return token ? `Bearer ${token}` : null;
  }

  /**
   * Clear all stored tokens (logout) from cookies
   */
  clearTokens(): void {
    try {
      clearImpersonationCookie();
      this.deleteCookie(this.ACCESS_TOKEN_KEY);
      this.deleteCookie(this.REFRESH_TOKEN_KEY);
      this.deleteCookie(this.TOKEN_DATA_KEY);

      console.log('[TOKEN-MANAGER] Tokens cleared successfully from cookies');
    } catch (error) {
      console.error('[TOKEN-MANAGER] Failed to clear tokens:', error);
    }
  }

  /**
   * Update only the access token (after refresh) in cookies
   */
  updateAccessToken(accessToken: string, expiresIn: number): void {
    try {
      const currentMetadata = this.getTokenMetadata();
      if (!currentMetadata) {
        console.error('[TOKEN-MANAGER] No existing token metadata to update');
        return;
      }

      const updatedMetadata: TokenMetadata = {
        ...currentMetadata,
        expiresIn,
        expiresAt: Date.now() + expiresIn * 1000,
      };

      // Store access token cookie with 30 days max-age to enable automatic refresh
      this.setCookie(this.ACCESS_TOKEN_KEY, accessToken, 30 * 24 * 60 * 60);
      this.setCookie(this.TOKEN_DATA_KEY, JSON.stringify(updatedMetadata), 30 * 24 * 60 * 60);

      const minutes = Math.floor(expiresIn / 60);
      console.log(`[TOKEN-MANAGER] Access token refreshed successfully (expires in ${minutes}m)`);
    } catch (error) {
      console.error('[TOKEN-MANAGER] Failed to update access token:', error);
    }
  }
}

// Export singleton instance
export const tokenManager = new TokenManager();

// Named exports for specific use cases
export { type TokenData, type TokenMetadata };

// Re-export AuthTokens from types package for convenience
export type { AuthTokens } from '@fluenceur/types';
